import React from "react";
import createCustomEvent from "../../../common/utils/createCustomEvent";
import Autocomplete from '@material-ui/lab/Autocomplete';
import throttle from 'lodash/throttle';
import axios from 'axios';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { CircularProgress, TextField, Typography } from "@material-ui/core";

interface IKeyValuePair {
    Key: number,
    Value: {
        IsProcessClosed: boolean,
        ProcessState: string,
        Subject: string,
    },
}

export interface IProps {
	refControl: string,
	errorText: string,
	name: string,
    onSet?: (value: IKeyValuePair) => void,
}

export default function RelatedIssueCombobox(props: IProps) {
    const [value, setValue] = React.useState<IKeyValuePair>(null);

	const issueIdControlRef = React.useRef<HTMLInputElement>();
	const titleControlRef = React.useRef<HTMLInputElement>();

	React.useEffect(() => {
		issueIdControlRef.current = document.querySelector("input[data-ref='" + props.refControl + "_issueId']");
		titleControlRef.current = document.querySelector("input[data-ref='" + props.refControl + "_title']");
		
		if (issueIdControlRef.current.value) {
            let newV: IKeyValuePair = {
                Key: parseInt(issueIdControlRef.current.value),
                Value: {
                    Subject: titleControlRef.current.value,
                    IsProcessClosed: false,
                    ProcessState: "",
                }
			}

			setValue(newV);
			if (props.onSet)
				props.onSet(newV);
		}
	}, []);

    return <IssuesForTaskAutocomplete
		className="qd-combocoxs-control"
		error={!!props.errorText}
		helperText={props.errorText}
		value={value}
		onChange={(v) => {
			setValue(v);

            issueIdControlRef.current.value = v?.Key.toString() ?? "";
            titleControlRef.current.value = v?.Value.Subject ?? "";

			let event = createCustomEvent("change");
            issueIdControlRef.current.dispatchEvent(event);

			if (props.onSet)
				props.onSet(v);
		}}
	/>
}

interface IIssuesForTaskAutocompleteProps {
    value: IKeyValuePair,
    onChange: (value: IKeyValuePair) => void,
    className?: string,
    error?: boolean,
    label?: string,
    helperText?: string,
}

function IssuesForTaskAutocomplete(props: IIssuesForTaskAutocompleteProps) {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [options, setOptions] = React.useState<IKeyValuePair[]>([]);
    const [inputValue, setInputValue] = React.useState('');
    const [cache, setCache] = React.useState({});

    const fetch = React.useMemo(
        () =>
            throttle((obj, callback) => {
                loadData(obj.input, callback);
            }, 500),
        [],
    );

    React.useEffect(() => {
        if (!open) {
            return;
        }
        if (cache[inputValue]) {
            setOptions(cache[inputValue]);
            return;
        }
        let active = true;
        fetch({ input: inputValue }, (results: IKeyValuePair[], searchText: string) => {
            if (active) {
                setOptions(results || []);

                let newCache = { ...cache };
                newCache[searchText] = results || [];
                setCache(newCache)
            }
        });

        return () => {
            active = false;
        };
    }, [inputValue, fetch, open]);

    function loadData(text: string, callback: (data: IKeyValuePair[], searchText: string) => void) {
        setIsLoading(true);
        axios
            .get<IKeyValuePair[]>("/Search/IssuesForTaskAutocomplete/", {
                params: {
                    text: text,
                }
            })
            .then((result) => {
                let results: IKeyValuePair[] = result.data;
                setIsLoading(false);
                callback(results, text);
            });
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
    };

    return (
        <Autocomplete
            className={clsx("mi-lab-autocomplete-outlined", props.className)}

            value={props.value}
            onChange={(event, newValue) => {
                let nv = newValue as IKeyValuePair;
                props.onChange(nv);
            }}

            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}

            getOptionSelected={(option, value) => option.Key === value.Key}
            getOptionLabel={option => `#${option.Key}: ${option.Value.Subject}`}
            options={options}
            loading={isLoading}

            clearText={t("common.action.clear")}
            noOptionsText={t("control.autocomplete.noOptionsMessage")}
            openText={t("common.action.open")}
            closeText={t("common.action.close")}
            loadingText={t("common.loading") + "..."}

            renderInput={params => (
                <TextField
                    {...params}
                    label={props.label}
                    variant="outlined"
                    onChange={handleChange}
                    error={props.error}
                    helperText={props.helperText}
                    fullWidth
                    InputProps={{
                        ...params.InputProps,
                        autoComplete: 'disabled',
                        endAdornment: (
                            <React.Fragment>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}

            renderOption={(option) => (<Typography color={option.Value.IsProcessClosed ? "textSecondary" : "textPrimary"}>
                #{option.Key}: {option.Value.Subject}
            </Typography>)}
        />
    );
}
